import rp from "@reporting/rp-app-host";
import { CellModel, CellRange, ColumnModel, CommentEvent, CommentSource, GridEvent, GridSortEvent, GridColumnResizeEvent, RowModel, TableCell } from "@omegagrid/grid";
import { Comment as CommentEntity, ContextData, ReportContext, TableViewOptions } from "@reporting/rp-types/analytics";
import { AnalyticsApplication, TableVisualization, getDefaultSections } from "@reporting/rp-mf-analytics";
import { TreeEvent, TreeMenu } from "@omegagrid/tree";
import { dom } from "@omegagrid/core";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { AxisItemProps, TableAdapter } from "./tableAdapter";
import { ModifyContextParams, ReportComment } from "../../types";
import { getIndicatorKey, parseIndicatorKey } from "../../common";
import { getCustomDetails, getDimensionMenuItems, getDimensionName, getDrillContainerId, openCustomDrill, openDrillDialog } from "./drillDown";
import { GridMenuEvent } from "@omegagrid/grid/dist/ui/grid.menu";
import { html, css } from "lit";

const app = () => rp.applications.get('analytics') as AnalyticsApplication;
const INLINE_BLOCK_SEPARATOR_COLOR = 'FFA500';
const INLINE_BLOCK_BACKGROUND_COLOR = 'rgba(255, 165, 0, 0.1)';

const getCellFilters = (cell: CellModel) => {
	const rowProps = cell?.row.properties as AxisItemProps;
	const colProps = cell?.column.properties as AxisItemProps;
	const filters: Record<string, string> = {};
	[...(rowProps?.items ?? []), ...(colProps?.items ?? [])].forEach(item => filters[item.dimNum] = item.id);
	return filters;
}

const getCellDescription = (cell: CellModel) => {
	const { row, column } = cell;
	const rowProps = row.properties as AxisItemProps;
	const colProps = column.properties as AxisItemProps;

	let description = '';
	if (rowProps?.items?.length) {
		description = rowProps.items.map(d => d.dim).join(' | ');
	}

	if (colProps?.items?.length) {
		if (rowProps?.items?.length) description += ' × ';
		description += colProps.items.map(d => d.dim).join(' | ');
	}

	return description;
}

export class TableController {

	private _cell: CellModel;
	private _tableCell: TableCell;
	private _dimensionMenu: TreeMenu;

	get grid() { return this.table.grid }

	get dimensionMenu() {
		if (this._dimensionMenu) return this._dimensionMenu;

		this._dimensionMenu = dom.createElement<TreeMenu>('og-tree-menu', {
			style: {width: '250px', zIndex: '1000'},
			listeners: [['select', (e: TreeEvent) => {
				this.openGroupBy(this._cell, e.node.key, 'dialog');
			}]]
		});

		this._dimensionMenu.setSourceData(getDimensionMenuItems(this.context.module as ModuleCode), {
			checkboxes: false,
			selectMode: 'single',
			customStyle: css``,
			nodeRenderer: (div, node) => html`
				<div style="flex: 1">${node.value}</div>
				<og-button icon="folder-closed" color="transparent" style="width: 20px" @mousedown="${(e: MouseEvent) => {
					e.stopPropagation();
					dom.hideElement(this._dimensionMenu);
					this.openGroupBy(this._cell, node.key , 'tab');
				}}"></og-button>
				<og-button icon="diagram-subtask" color="transparent" style="width: 20px" @mousedown="${(e: MouseEvent) => {
					e.stopPropagation();
					dom.hideElement(this._dimensionMenu);
					this.openGroupBy(this._cell, node.key, 'inline');
				}}"></og-button>
			`,
		});

		rp.host.append(this._dimensionMenu);

		return this._dimensionMenu;
	}

	async openInlineGroupBy(cell: CellModel, params: ModifyContextParams) {
		const loader = dom.createElement('og-loader', {
			style: {position: 'absolute', right: '4px', top: '4px'}
		});
		this._tableCell.appendChild(loader);

		const groupByContext = app().contextBroker.modifyContext(this.context, params);
		const result = await app().reportBroker.getReportData(groupByContext);
		const tempAdapter = new TableAdapter(result, groupByContext);

		const rowData = await tempAdapter.getRows();
		loader.remove();
		if (!rowData?.length) return;

		rowData.forEach((r, i) => r.s = {
			top: i == 0 ? {color: INLINE_BLOCK_SEPARATOR_COLOR} : undefined,
			cssStyle: {backgroundColor: INLINE_BLOCK_BACKGROUND_COLOR},
		});

		if (cell.row.next) {
			cell.row.next.setStyleProperties({top: {color: INLINE_BLOCK_SEPARATOR_COLOR}});
			cell.row.next.cells?.forEach(c => c.invalidateStyle());
		}

		this.grid.model.insertRowData(cell.row, 0, rowData);
		this.grid.rendering.renderAll(true);
	}

	openGroupBy(cell: CellModel, dim: string, target: 'dialog'|'tab'|'inline' = 'dialog') {
		const params: ModifyContextParams = {
			filters: getCellFilters(cell),
			dim: dim
		}

		let caption = getCellDescription(cell);
		const dimensionName = getDimensionName(this.context.module as ModuleCode, dim);
		if (dimensionName) caption = `${dimensionName} › ${caption}`;

		const id = getDrillContainerId(this.context, params);

		if (target == 'dialog') {
			openDrillDialog(id, caption);
		} else if (target == 'tab') {
			rp.host.open(id, {title: caption});
		} else {
			this.openInlineGroupBy(cell, params);
		}
	}

	/** right click cell menu with the custom drills defined for the module (and statement) */
	private initCustomDrillMenu() {
		const details = getCustomDetails(this.context);
		if (!details.length) return;

		const hidden = (cell: CellModel) => this.table.themeEditMode
			|| Object.keys(getCellFilters(cell)).length == 0;

		this.grid.options.menus = {
			row: {items: []},
			column: {items: []},
			cell: {
				items: details.map(d => ({
					key: `custom_${d.id_custom_resource}`,
					value: d.name,
					icon: 'puzzle-piece',
					hidden,
				})),
			},
		};

		this.grid.addEventListener('grid.menu.click', (e: GridMenuEvent) => {
			const detail = details.find(d => `custom_${d.id_custom_resource}` == e.item?.key);
			if (!detail || !e.cell) return;

			const params: ModifyContextParams = {filters: getCellFilters(e.cell)};
			const indicator = (e.cell.column.properties as AxisItemProps)?.indicator
				?? (e.cell.row.properties as AxisItemProps)?.indicator;
			if (indicator) params.indicator = indicator.index;

			openCustomDrill(this.context, detail, params, getCellDescription(e.cell));
		});
	}

	// #region cell comments

	/** cell addresses currently holding comments in the grid model */
	private _appliedCommentAddresses = new Set<string>();
	/** local changes of the loaded comments, keyed by the loaded (base) idz; null = deleted */
	private _commentOverlay = new Map<number, ReportComment|null>();
	/** comments created in this session (not part of result.comments) */
	private _addedComments: ReportComment[] = [];

	/** wires the og native comment popup to the comments API */
	private async initComments() {
		const grid = this.grid;
		await grid.updateComplete;
		if (!grid.comment) return;

		// remember the addresses the adapter's initial getComments occupied,
		// so a later remap can clear them
		(this.table.adapter?.getComments() ?? []).forEach(source => {
			this._appliedCommentAddresses.add(new CellRange(source.c, source.r).A1);
		});

		grid.comment.addEventListener('comment.commit', (e: CommentEvent) => this.onCommentCommit(e));
		grid.comment.addEventListener('comment.delete', (e: CommentEvent) => this.onCommentDelete(e));
	}

	private toReportComment(saved: CommentEntity): ReportComment {
		return {
			idz: saved.idz,
			rowKey: saved.row_key,
			columnKey: saved.column_key,
			text: saved.text,
			whoCreated: saved.who_created,
			dateCreated: saved.date_created,
		};
	}

	private async onCommentCommit(e: CommentEvent) {
		const rowKey = (e.cell.row.properties as AxisItemProps)?.key;
		const columnKey = (e.cell.column.properties as AxisItemProps)?.key;
		if (rowKey == null || columnKey == null) return;

		const contextData = this.table.contextData as ContextData;
		const idz = e.comment.properties?.idz as number|undefined;

		try {
			const saved = await rp.ms('analytics').post<CommentEntity>('comments', {
				idz: idz,
				module: contextData.module,
				filters: contextData.filters,
				row_key: rowKey,
				column_key: columnKey,
				text: e.comment.text,
			});

			// an edit archives the old row and creates a new idz
			e.comment.properties = {...e.comment.properties, idz: saved.idz};
			this.recordSavedComment(idz, this.toReportComment(saved));
			this._appliedCommentAddresses.add(e.cell.address);
		} catch (error) {
			console.error('Comment save failed', error);
		}
	}

	private async onCommentDelete(e: CommentEvent) {
		const idz = e.comment.properties?.idz as number|undefined;
		if (idz) {
			try {
				await rp.ms('analytics').delete(`comments/${idz}`);
			} catch (error) {
				console.error('Comment delete failed', error);
				return;
			}
			this.recordDeletedComment(idz);
		}

		// og syncs the model on commit, but not on delete
		this.grid.model.comments.set(e.cell.address, e.items);
		e.cell.invalidateStyle();
		this.grid.rendering.updateCells([e.cell]);
	}

	private recordSavedComment(previousIdz: number|undefined, comment: ReportComment) {
		if (!previousIdz) {
			this._addedComments.push(comment);
			return;
		}
		// an edit - replace whichever version the previous idz points to
		const added = this._addedComments.findIndex(c => c.idz == previousIdz);
		if (added >= 0) {
			this._addedComments[added] = comment;
			return;
		}
		for (const [baseIdz, current] of this._commentOverlay) {
			if (current?.idz == previousIdz) {
				this._commentOverlay.set(baseIdz, comment);
				return;
			}
		}
		this._commentOverlay.set(previousIdz, comment);
	}

	private recordDeletedComment(idz: number) {
		const added = this._addedComments.findIndex(c => c.idz == idz);
		if (added >= 0) {
			this._addedComments.splice(added, 1);
			return;
		}
		for (const [baseIdz, current] of this._commentOverlay) {
			if (current?.idz == idz) {
				this._commentOverlay.set(baseIdz, null);
				return;
			}
		}
		this._commentOverlay.set(idz, null);
	}

	/** loaded comments with the local overlay (edits, deletes, additions) applied */
	private effectiveComments(): ReportComment[] {
		const base = this.table.adapter?.result.comments ?? [];
		return [
			...base.map(c => this._commentOverlay.has(c.idz) ? this._commentOverlay.get(c.idz) : c).filter(Boolean),
			...this._addedComments,
		];
	}

	/**
	 * Comments are keyed by cell address which is derived from the live row
	 * index - a local sort reindexes the rows, so the comment map must be
	 * rebuilt from the row/column keys.
	 */
	private remapComments() {
		const model = this.grid.model;
		const comments = this.effectiveComments();
		if (!this._appliedCommentAddresses.size && !comments.length) return;

		const rowsByIndex = new Map<number, RowModel>();
		const rowKeyIndex = new Map<string, RowModel>();
		for (const row of model.rows) {
			rowsByIndex.set(row.index, row);
			const key = (row.properties as AxisItemProps)?.key;
			if (key != null) rowKeyIndex.set(key, row);
		}
		const columnKeyIndex = new Map<string, ColumnModel>();
		for (const column of model.columns) {
			const key = (column.properties as AxisItemProps)?.key;
			if (key != null) columnKeyIndex.set(key, column);
		}

		const affected: CellModel[] = [];

		for (const address of this._appliedCommentAddresses) {
			model.comments.delete(address);
			const range = CellRange.fromA1(address);
			const cell = rowsByIndex.get(range.r1)?.cell(range.c1);
			if (cell) {
				cell.invalidateStyle();
				affected.push(cell);
			}
		}
		this._appliedCommentAddresses.clear();

		const userId = rp.userProvider.id;
		const isAdmin = rp.userProvider.hasRight('admin');
		const byCell = new Map<string, {cell: CellModel, sources: CommentSource[]}>();

		for (const comment of comments) {
			const row = rowKeyIndex.get(comment.rowKey);
			const column = columnKeyIndex.get(comment.columnKey);
			const cell = row && column ? row.cell(column.index) : null;
			if (!cell) continue;

			const entry = byCell.get(cell.address) ?? {cell, sources: []};
			entry.sources.push({
				text: comment.text,
				date: comment.dateCreated,
				author: {id: comment.whoCreated, name: comment.whoCreated},
				readOnly: comment.whoCreated != userId && !isAdmin,
				props: {idz: comment.idz},
			});
			byCell.set(cell.address, entry);
		}

		for (const [address, entry] of byCell) {
			model.comments.set(address, entry.sources);
			this._appliedCommentAddresses.add(address);
			entry.cell.invalidateStyle();
			affected.push(entry.cell);
		}

		if (affected.length) this.grid.rendering.updateCells(affected);
	}

	// #endregion

	openDetail(cell: CellModel) {
		const indicator =(cell.column.properties as AxisItemProps).indicator ?? (cell.row.properties as AxisItemProps).indicator;
		const props: ModifyContextParams = {filters: getCellFilters(cell)};
		if (indicator) props.indicator = indicator.index;

		openDrillDialog(getDrillContainerId(this.context, props), getCellDescription(cell));
	}

	getViewOptions(): TableViewOptions {
		const contextData = this.table.contextData as ContextData;
		contextData.view_options = contextData.view_options || {};
		return contextData.view_options as TableViewOptions;
	}

	constructor(readonly table: TableVisualization, readonly context: ReportContext) {
		const grid = table.grid;
		this.initCustomDrillMenu();
		this.initComments();

		grid.addEventListener('grid.click', async (e: GridEvent) => {
			if (table.themeEditMode) return;
			this._cell = e.cell;
			if (!e.cell?.clickable) return;

			const { column, row } = e.cell;
			const colProps = column.properties as AxisItemProps;
			const rowProps = row.properties as AxisItemProps;

			this._tableCell = grid.table.getCellByCellModel(e.cell);

			if ((colProps?.indicator && rowProps?.items?.length) || (rowProps?.indicator && colProps?.items?.length)) {
				if (e.cell.clickable) this.openDetail(e.cell);
			} else {
				await this.dimensionMenu.updateComplete;
				this.dimensionMenu.open(this._tableCell, 'right');
			}
		});

		table.addEventListener('disconnected', () => {
			this._dimensionMenu?.remove();
			this._dimensionMenu = null;
		});

		grid.addEventListener('grid.column.resize', (e: GridColumnResizeEvent) => {
			const viewOptions = this.getViewOptions();
			viewOptions.columns = viewOptions.columns || {};
			viewOptions.columns[e.column.index] = viewOptions.columns[e.column.index] || {};
			viewOptions.columns[e.column.index].w = e.column.width;
			table.dispatchEditEvent();
		});

		grid.addEventListener('grid.sort', (e: GridSortEvent) => {
			const viewOptions = this.getViewOptions();
			viewOptions.sorting = !e.sortSettings || e.sortSettings.length == 0 ? undefined : e.sortSettings;
			table.dispatchEditEvent();
			// the sort reindexed the rows - comments are keyed by the live index
			this.remapComments();
		});
	}
}
